import { useState, useEffect, useCallback } from 'react';
import {
  fetchUserBrandKitPresets,
  fetchActiveBrandKit
} from '../services/brandKitService';

/**
 * 통합 브랜드키트 훅 - 색상과 폰트를 함께 관리
 * @param {string} userId - 사용자명
 * @param {number} brandKitUpdateTrigger - 브랜드키트 업데이트 트리거
 * @returns {Object} { colors, fonts, setColors, setFonts, loadBrandKitColors, loadBrandKitFonts, presets, activePresetId, loadAllPresets }
 */
export const useBrandKit = (userId, brandKitUpdateTrigger) => {
  const [colors, setColors] = useState([]);
  const [fonts, setFonts] = useState([]);
  const [presets, setPresets] = useState({});
  const [activePresetId, setActivePresetId] = useState('preset1');

  // 브랜드키트 색상 로드 함수 (Firestore 연동)
  const loadBrandKitColors = useCallback(async () => {
    try {
      if (userId) {
        // Firestore에서 활성 브랜드키트 로드
        const brandKit = await fetchActiveBrandKit(userId);
        const colors = brandKit.colors || [];
        return colors;
      }

      // userId이 없으면 localStorage 사용 (fallback)
      const savedBrandKit = localStorage.getItem('brandKit');
      if (savedBrandKit) {
        const parsed = JSON.parse(savedBrandKit);
        return parsed.colors && parsed.colors.length > 0 ? parsed.colors : [];
      }
      return [];
    } catch (error) {
      console.error('브랜드키트 색상 로드 오류:', error);
      // 오류 시 localStorage fallback
      try {
        const savedBrandKit = localStorage.getItem('brandKit');
        if (savedBrandKit) {
          const parsed = JSON.parse(savedBrandKit);
          return parsed.colors || [];
        }
      } catch (fallbackError) {
        console.error('localStorage fallback 오류:', fallbackError);
      }
      return [];
    }
  }, [userId]);

  // 브랜드키트 폰트 로드 함수 (Firestore 연동)
  const loadBrandKitFonts = useCallback(async () => {
    try {
      if (userId) {
        // Firestore에서 활성 브랜드키트 로드
        const brandKit = await fetchActiveBrandKit(userId);
        const fonts = brandKit.fonts || [];
        return fonts;
      }

      // userId이 없으면 localStorage 사용 (fallback)
      const savedBrandKit = localStorage.getItem('brandKit');
      if (savedBrandKit) {
        const parsed = JSON.parse(savedBrandKit);
        return parsed.fonts && parsed.fonts.length > 0 ? parsed.fonts : [];
      }
      return [];
    } catch (error) {
      console.error('브랜드키트 폰트 로드 오류:', error);
      // 오류 시 localStorage fallback
      try {
        const savedBrandKit = localStorage.getItem('brandKit');
        if (savedBrandKit) {
          const parsed = JSON.parse(savedBrandKit);
          return parsed.fonts || [];
        }
      } catch (fallbackError) {
        console.error('localStorage fallback 오류:', fallbackError);
      }
      return [];
    }
  }, [userId]);

  // 모든 프리셋 로드 함수
  const loadAllPresets = useCallback(async () => {
    try {
      if (userId) {
        const { presets, activePresetId } = await fetchUserBrandKitPresets(userId);
        setPresets(presets);
        setActivePresetId(activePresetId);

        // 활성 프리셋의 색상과 폰트 설정
        const activePreset = presets[activePresetId];
        if (activePreset) {
          setColors(activePreset.colors || []);
          setFonts(activePreset.fonts || []);
        }

        return { presets, activePresetId };
      }
      return { presets: {}, activePresetId: 'preset1' };
    } catch (error) {
      console.error('프리셋 로드 오류:', error);
      return { presets: {}, activePresetId: 'preset1' };
    }
  }, [userId]);

  // 초기 로드 (userId 변경 시에도 재로드)
  useEffect(() => {
    const initBrandKit = async () => {
      if (userId) {
        // 프리셋 구조 사용 시 전체 프리셋 로드
        await loadAllPresets();
      } else {
        // 기존 방식으로 로드
        const brandKitColors = await loadBrandKitColors();
        const brandKitFonts = await loadBrandKitFonts();
        setColors(brandKitColors);
        setFonts(brandKitFonts);
      }
    };
    initBrandKit();
  }, [userId, loadBrandKitColors, loadBrandKitFonts, loadAllPresets]);

  // 브랜드키트 업데이트 트리거 감지
  useEffect(() => {
    if (brandKitUpdateTrigger > 0) {
      const updateBrandKit = async () => {
        if (userId) {
          await loadAllPresets();
        } else {
          const brandKitColors = await loadBrandKitColors();
          const brandKitFonts = await loadBrandKitFonts();
          setColors(brandKitColors);
          setFonts(brandKitFonts);
        }
      };
      updateBrandKit();
    }
  }, [brandKitUpdateTrigger, userId, loadBrandKitColors, loadBrandKitFonts, loadAllPresets]);

  // localStorage 변경 감지 (브랜드키트 업데이트 시)
  useEffect(() => {
    const handleStorageChange = async () => {
      if (userId) {
        await loadAllPresets();
      } else {
        const brandKitColors = await loadBrandKitColors();
        const brandKitFonts = await loadBrandKitFonts();
        setColors(brandKitColors);
        setFonts(brandKitFonts);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [userId, loadBrandKitColors, loadBrandKitFonts, loadAllPresets]);

  return {
    colors,
    fonts,
    setColors,
    setFonts,
    loadBrandKitColors,
    loadBrandKitFonts,
    presets,
    activePresetId,
    loadAllPresets
  };
};
